import React, { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, useMap } from "react-leaflet";
import L from "leaflet";

// Fix for default marker icons in Leaflet with React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png"
});

const DEFAULT_CENTER = [58, -4.5];
const DEFAULT_ZOOM = 7;

const BASE_MARKER_SIZE = 16;
const DEFAULT_MAX_ZOOM = 13;

const defaultBothyIcon = L.divIcon({
  className: "bothy-marker-icon leaflet-div-icon",
  iconSize: [BASE_MARKER_SIZE, BASE_MARKER_SIZE],
  iconAnchor: [BASE_MARKER_SIZE / 2, BASE_MARKER_SIZE / 2],
  popupAnchor: [0, -BASE_MARKER_SIZE / 2]
});

const selectedBothyIcon = L.divIcon({
  className: "bothy-marker-icon is-selected leaflet-div-icon",
  iconSize: [BASE_MARKER_SIZE + 6, BASE_MARKER_SIZE + 6],
  iconAnchor: [(BASE_MARKER_SIZE + 6) / 2, (BASE_MARKER_SIZE + 6) / 2],
  popupAnchor: [0, -(BASE_MARKER_SIZE + 6) / 2]
});

const MapFitter = ({ bothies }) => {
  const map = useMap();

  useEffect(() => {
    const coords = bothies
      .map(b => [Number(b.lat), Number(b.lon)])
      .filter(([lat, lon]) => Number.isFinite(lat) && Number.isFinite(lon));

    if (coords.length === 0) {
      map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
      return;
    }

    if (coords.length === 1) {
      map.setView(coords[0], DEFAULT_MAX_ZOOM);
      return;
    }

    const bounds = L.latLngBounds(coords);
    map.fitBounds(bounds, { padding: [32, 32], maxZoom: DEFAULT_MAX_ZOOM });
  }, [bothies, map]);

  return null;
};

const SelectedBothyFocus = ({ selectedBothy }) => {
  const map = useMap();

  useEffect(() => {
    if (!selectedBothy) return;

    const lat = Number(selectedBothy.lat);
    const lon = Number(selectedBothy.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;

    const currentZoom = map.getZoom();
    const targetZoom = Math.min(DEFAULT_MAX_ZOOM, Math.max(currentZoom, 12));
    map.setView([lat, lon], targetZoom, { animate: true });
  }, [map, selectedBothy]);

  return null;
};

const MapView = ({ bothies, selectedBothy, onSelectBothy }) => {
  const hasBothies = bothies.length > 0;
  const copyCoords = (lat, lon) => {
    const text = `${Number(lat).toFixed(6)}, ${Number(lon).toFixed(6)}`;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {});
    } else {
      const temp = document.createElement("textarea");
      temp.value = text;
      temp.style.position = "fixed";
      temp.style.opacity = "0";
      document.body.appendChild(temp);
      temp.select();
      try {
        document.execCommand("copy");
      } catch (_) {
        // ignore
      }
      document.body.removeChild(temp);
    }
  };

  return (
    <div className="map-container">
      <MapContainer center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM} scrollWheelZoom>
        <MapFitter bothies={bothies} />
        <SelectedBothyFocus selectedBothy={selectedBothy} />
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        {bothies.map(bothy => (
          <Marker
            key={bothy.id}
            position={[bothy.lat, bothy.lon]}
            icon={selectedBothy?.id === bothy.id ? selectedBothyIcon : defaultBothyIcon}
            eventHandlers={{
              click: () => onSelectBothy(bothy)
            }}
          >
            <Popup className="bothy-popup">
              <div className="bothy-popup__header">
                <h4>{bothy.name}</h4>
              </div>
              <div className="bothy-popup__meta">
                <span>{Number(bothy.elevation).toFixed(0)} m</span>
                <button
                  type="button"
                  className="copyable"
                  title="Copy lat/lon"
                  onClick={() => copyCoords(bothy.lat, bothy.lon)}
                >
                  {Number(bothy.lat).toFixed(4)}, {Number(bothy.lon).toFixed(4)}
                </button>
              </div>
            </Popup>
          </Marker>
        ))}

        {selectedBothy?.nearestRoad?.location && (
          <CircleMarker
            center={[
              selectedBothy.nearestRoad.location.lat,
              selectedBothy.nearestRoad.location.lon
            ]}
            radius={10}
          />
        )}
      </MapContainer>

      {!hasBothies && (
        <div className="map-empty-message">
          No bothies match the current filters. Try broadening your search.
        </div>
      )}
    </div>
  );
};

export default MapView;
